/**
 * @file apps/web/src/components/UploadZone.tsx
 * @description Drag-and-drop audio file upload zone.
 * Accepts audio/video files, submits via multipart POST to /audio/upload.
 * POST /audio/upload returns as soon as the file is stored (not once the
 * pipeline finishes — that runs in the background, potentially for
 * minutes on a long recording). onComplete fires with that quick kickoff
 * result; the caller (UatPage.tsx) renders ProcessingStatus separately to
 * poll GET /audio/:id for the actual transcription/requirements/test-
 * script generation progress — this component's job ends at "the upload
 * itself succeeded and a job now exists to poll."
 */

import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileAudio, X, AlertCircle } from 'lucide-react';
import type { AudioUploadKickoff } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UploadZoneProps {
  projectId:   string;
  onComplete?: (result: AudioUploadKickoff) => void;
  onError?:    (err: Error) => void;
  uploadFn:    (formData: FormData) => Promise<AudioUploadKickoff>;
  className?:  string;
}

type UploadPhase = 'idle' | 'ready' | 'uploading' | 'error';

// ---------------------------------------------------------------------------
// File info display
// ---------------------------------------------------------------------------

function FileInfo({ file, onRemove }: { file: File; onRemove: () => void }) {
  const sizeMB = (file.size / 1024 / 1024).toFixed(1);

  return (
    <div className="flex items-center gap-3 p-3 bg-sky rounded-lg">
      <FileAudio size={20} className="text-blue shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">{file.name}</p>
        <p className="text-[11px] font-mono text-muted">{sizeMB} MB · {file.type}</p>
      </div>
      <button onClick={onRemove} className="btn-icon text-muted hover:text-danger">
        <X size={14} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UploadZone
// ---------------------------------------------------------------------------

export function UploadZone({ projectId, onComplete, onError, uploadFn, className = '' }: UploadZoneProps) {
  const [phase, setPhase]   = useState<UploadPhase>('idle');
  const [file, setFile]     = useState<File | null>(null);
  const [errMsg, setErrMsg] = useState<string>('');

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      setPhase('ready');
      setErrMsg('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.webm', '.mp4'],
      'video/*': ['.mp4', '.webm', '.mov'],
    },
    maxFiles: 1,
    disabled: phase === 'uploading',
  });

  const handleUpload = async () => {
    if (!file) return;
    setPhase('uploading');
    setErrMsg('');

    const fd = new FormData();
    // projectId before file, deliberately: the API streams the file
    // straight to storage as it arrives (large recordings, no full-buffer
    // read), so it needs projectId already in hand to build the
    // tenant-scoped storage path before the file part starts — see
    // apps/api/src/routes/audio.ts.
    fd.append('projectId', projectId);
    fd.append('file', file);

    try {
      const kickoff = await uploadFn(fd);
      onComplete?.(kickoff);
      // Reset back to idle immediately — the caller takes over tracking
      // progress via ProcessingStatus from here.
      setPhase('idle');
      setFile(null);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setErrMsg(e.message);
      setPhase('error');
      onError?.(e);
    }
  };

  const reset = () => {
    setPhase('idle');
    setFile(null);
    setErrMsg('');
  };

  return (
    <div className={['space-y-3', className].join(' ')}>
      {/* Drop zone */}
      {phase === 'idle' && (
        <div
          {...getRootProps()}
          className={[
            'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
            isDragActive
              ? 'border-blue bg-sky/40'
              : 'border-[rgba(8,17,31,0.12)] hover:border-blue/50 hover:bg-sky/20',
          ].join(' ')}
        >
          <input {...getInputProps()} />
          <Upload size={28} className={['mx-auto mb-3', isDragActive ? 'text-blue' : 'text-muted'].join(' ')} />
          <p className="text-sm font-medium text-ink">
            {isDragActive ? 'Drop the file here' : 'Drag & drop a recording, or click to browse'}
          </p>
          <p className="text-xs font-mono text-muted mt-1.5">
            MP3, WAV, M4A, WebM, MP4 · max 200 MB
          </p>
        </div>
      )}

      {/* File ready / error */}
      {(phase === 'ready' || phase === 'error') && file && (
        <div className="space-y-3">
          <FileInfo file={file} onRemove={reset} />
          {errMsg && (
            <div className="flex items-start gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              {errMsg}
            </div>
          )}
          <button onClick={handleUpload} className="btn-primary w-full justify-center">
            <Upload size={15} />
            Run Upload Pipeline
          </button>
        </div>
      )}

      {/* Uploading — this only covers the (fast, streamed) file transfer
          itself, not transcription. ProcessingStatus (rendered by the
          caller once onComplete fires) tracks the rest. */}
      {phase === 'uploading' && file && (
        <div className="space-y-3">
          <FileInfo file={file} onRemove={() => {}} />
          <div className="flex items-center gap-2.5 text-xs font-mono text-blue font-semibold">
            <span className="w-2 h-2 rounded-full bg-blue animate-pulse" />
            Uploading file…
          </div>
        </div>
      )}
    </div>
  );
}
